import {CommonModule} from '@angular/common';
import {provideHttpClient, withInterceptors, withXhr} from '@angular/common/http';
import {ErrorHandler, NgModule, provideAppInitializer} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {RouterModule} from '@angular/router';
import {provideDatadogRouter} from '@datadog/browser-rum-angular';
import {EffectsModule} from '@ngrx/effects';
import {StoreRouterConnectingModule} from '@ngrx/router-store';
import {StoreModule} from '@ngrx/store';
import {StoreDevtoolsModule} from '@ngrx/store-devtools';
import {SeAnalyticsModule} from 'se-analytics';
import {SeFeApiModule} from 'se-fe-api';
import {SeFeAttachmentServiceModule} from 'se-fe-attachment-service';
import {SeFeFrameRouterConfig, SeFeFrameRouterModule} from 'se-fe-frame-router';
import {
  seFeIconCheck,
  seFeIconCheckCircleOutline,
  seFeIconClose,
  seFeIconDollar,
  seFeIconDotFill,
  seFeIconEdit,
  seFeIconError,
  seFeIconFilterList,
  seFeIconGripVertical,
  seFeIconInfoCircle,
  SeFeIconModule,
  seFeIconMore,
  seFeIconMoreVertical,
  seFeIconPencilFill,
  seFeIconPlusCircleOutline,
  seFeIconSpotClipboard,
  seFeIconSpotGearOne,
  seFeIconSpotPricetag,
  seFeIconSpotVariants,
  seFeIconStoreThin,
  seFeIconTrash,
} from 'se-fe-icon';
import {SeFeIndicatorDotModule} from 'se-fe-indicator-dot';
import {SeFeMenuModule} from 'se-fe-menu';
import {SeFeOverlayModule} from 'se-fe-overlay';
import {SeFeSpinnerModule} from 'se-fe-spinner';
import {SeFeToastModule} from 'se-fe-toast';
import {environment} from '../environments/environment';
import {AppComponent} from './app.component';
import {appRoutes} from './app.routes';
import {ErrorHandlingEffects} from './error/error-handling/error-handling.effects';
import {ErrorHandlingModule} from './error/error-handling/error-handling.module';
import {GlobalErrorHandler} from './error/error-handling/global-error-handler.service';
import {notFoundInterceptor} from './error/error-handling/not-found.interceptor';
import {BundleListEffects} from './features/bundle-list/store/bundle-list.effects';
import {CreateBundleEffects} from './features/bundle/components/create-bundle/store/create-bundle.effects';
import {
  CREATE_BUNDLE_FEATURE_KEY,
  createBundleReducer,
} from './features/bundle/components/create-bundle/store/create-bundle.reducer';
import {CatalogDetailEffects} from './features/catalog-detail/store/catalog-detail.effects';
import {CATALOG_DETAIL_STATE_KEY, catalogDetailReducer} from './features/catalog-detail/store/catalog-detail.reducer';
import {CatalogEffects} from './features/catalog-list/store/catalog.effects';
import {CATALOG_STATE_KEY, catalogReducer} from './features/catalog-list/store/catalog.reducer';
import {ProductListEffects} from './features/product-list/store/product-list.effects';
import {CreateProductEffects} from './features/product/components/create-product/store/create-product.effects';
import {
  CREATE_PRODUCT_FEATURE_KEY,
  createProductReducer,
} from './features/product/components/create-product/store/create-product.reducer';
import {buildAnalyticsConfig} from './observability/analytics.config';
import {initializeObservability} from './observability/observability.initializer';
import {metaReducers, ROOT_REDUCERS} from './reducers';
import {OrganizationEffects} from './state/organization/organization.effects';
import {ORGANIZATION_FEATURE_KEY, organizationReducer} from './state/organization/organization.reducer';
import {RouterEffects} from './state/router/router.effects';

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserAnimationsModule,
    BrowserModule,
    CommonModule,
    EffectsModule.forRoot([BundleListEffects, ErrorHandlingEffects, ProductListEffects, RouterEffects]),
    EffectsModule.forFeature(
      CreateProductEffects,
      CreateBundleEffects,
      OrganizationEffects,
      CatalogEffects,
      CatalogDetailEffects
    ),
    ErrorHandlingModule,
    RouterModule.forRoot(appRoutes, {
      initialNavigation: 'enabledBlocking',
      bindToComponentInputs: true,
      paramsInheritanceStrategy: 'always',
    }),
    SeAnalyticsModule.forRoot(buildAnalyticsConfig()),
    SeFeApiModule.forRoot({
      domainEnvironments: environment.domainEnvironments,
    }),
    SeFeAttachmentServiceModule.forRoot(environment),
    SeFeIconModule.include([
      seFeIconCheck,
      seFeIconCheckCircleOutline,
      seFeIconClose,
      seFeIconDollar,
      seFeIconDotFill,
      seFeIconEdit,
      seFeIconError,
      seFeIconFilterList,
      seFeIconGripVertical,
      seFeIconInfoCircle,
      seFeIconMore,
      seFeIconMoreVertical,
      seFeIconPencilFill,
      seFeIconPlusCircleOutline,
      seFeIconSpotClipboard,
      seFeIconSpotGearOne,
      seFeIconSpotPricetag,
      seFeIconSpotVariants,
      seFeIconStoreThin,
      seFeIconTrash,
      seFeIconSpotPricetag,
    ]),
    StoreModule.forRoot(ROOT_REDUCERS, {metaReducers}),
    StoreModule.forFeature(ORGANIZATION_FEATURE_KEY, organizationReducer),
    StoreModule.forFeature(CATALOG_STATE_KEY, catalogReducer),
    StoreModule.forFeature(CATALOG_DETAIL_STATE_KEY, catalogDetailReducer),
    StoreModule.forFeature(CREATE_PRODUCT_FEATURE_KEY, createProductReducer),
    StoreModule.forFeature(CREATE_BUNDLE_FEATURE_KEY, createBundleReducer),
    StoreRouterConnectingModule.forRoot(),
    !environment.production ? StoreDevtoolsModule.instrument() : [],
    SeFeToastModule,
    SeFeMenuModule,
    SeFeSpinnerModule,
    SeFeOverlayModule,
    SeFeIndicatorDotModule,
    SeFeFrameRouterModule.forRoot({
      origin: environment.seFeFrameRouterOrigin,
      redirectUrl: environment.seFeFrameRouterRedirectUrl,
    } as SeFeFrameRouterConfig),
  ],
  providers: [
    provideAppInitializer(() => {
      initializeObservability();
    }),
    {provide: ErrorHandler, useClass: GlobalErrorHandler},
    provideDatadogRouter(),
    provideHttpClient(withXhr(), withInterceptors([notFoundInterceptor])),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
